import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';

export type AiFeature =
  | 'shiftBrief'
  | 'useOfForceRadar'
  | 'story'
  | 'unitPatterns';

interface AiFeatureDescription {
  title: string;
  sections: { heading: string; points: string[] }[];
}

const testDataHeading = 'Testdata – det skal være registreret';

const descriptions: Record<AiFeature, AiFeatureDescription> = {
  shiftBrief: {
    title: 'Om vagt-brief',
    sections: [
      {
        heading: 'Hvad gør den?',
        points: [
          'Giver dig et hurtigt overblik over borgeren, før du går ind på vagt – særligt nyttigt for vikarer og nattevagter.',
          'Husk, trafiklys, aftaler i dag og de næste 3 dage og aktive delmål hentes direkte fra Fasit. Det er ikke AI.',
          'Kun afsnittet "Siden sidst · 72 timer" er skrevet af AI.',
        ],
      },
      {
        heading: 'Hvad bygger den på?',
        points: [
          'Dagbogsnotater, observationer og medicinudleveringer fra de seneste 72 timer.',
          'Medicin kun for ordinationer med lokal opsætning.',
          'Hvert punkt viser, hvilke kilder det bygger på. Klik på en kilde for at se den.',
        ],
      },
      {
        heading: 'Det skal du selv vurdere',
        points: [
          "AI'en må kun gengive kilderne, men den kan overse eller forenkle noget. Tjek kilden, hvis et punkt er vigtigt for vagten.",
          'Det, der ikke er registreret i Fasit, kender briefen ikke.',
        ],
      },
      {
        heading: testDataHeading,
        points: [
          '"Siden sidst": mindst én registrering på enheden inden for de seneste 72 timer – et aktivt dagbogsnotat, en observation, en medicinudlevering eller en delmålsopfølgning. Uden dem laves der ingen AI-tekst.',
          'Husk: en obs-note eller note på borgeren hos enheden, en CAVE-oplysning på borgerens stamdata eller et Skema 1 i kladde eller sendt til lederen.',
          'Trafiklys: et udfyldt trafiklys med en vurdering på enheden. Indikatorer, hvad der hjælper, gør/undgå og (i rød) hvem der kontaktes, vises fra den aktuelle zone.',
          'Aftaler: begivenheder i borgerens kalender i dag og de næste 3 dage, som ikke er aflyst.',
          'Delmål: en aktuel bestilling på enheden med et aktuelt delmål. Opfølgninger fra de seneste 72 timer kommer med i "Siden sidst".',
          'Medicin: en ordination på medicinkortet med lokal opsætning og en udlevering inden for 72 timer. Kræver rollen Medicin læse.',
        ],
      },
      {
        heading: 'Data',
        points: [
          'Briefen laves, når du åbner den, og gemmes ikke. Tryk på pilene for at lave den igen.',
        ],
      },
    ],
  },
  useOfForceRadar: {
    title: 'Om magtanvendelses-radaren',
    sections: [
      {
        heading: 'Hvad gør den?',
        points: [
          'Gennemgår borgerens dagbog og finder notater, der beskriver et fysisk indgreb eller et andet indgreb i selvbestemmelsesretten (serviceloven kapitel 24).',
          'Formålet er at sikre, at alle magtanvendelser bliver registreret på Skema 1.',
        ],
      },
      {
        heading: 'Hvad bygger den på?',
        points: [
          'Dagbogsnotater fra de seneste 30 dage og de Skema 1, der allerede er oprettet for borgeren.',
          "Hvert fund viser et ordret citat fra notatet, et forslag til paragraf og hvor sikker AI'en er.",
          'Fund, der allerede er dækket af et Skema 1 fra samme episode, er foldet sammen.',
        ],
      },
      {
        heading: 'Det skal du selv vurdere',
        points: [
          'Radaren afgør ikke, om der er tale om magtanvendelse. Det gør du og lederen.',
          '"Opret Skema 1" udfylder kun tidspunktet. Beskrivelsen af episoden skal være din egen.',
          'Radaren kan overse notater og finde notater, der ikke er indgreb.',
        ],
      },
      {
        heading: testDataHeading,
        points: [
          "Aktive dagbogsnotater på enheden med en hændelsesdato inden for de seneste 30 dage. Uden notater kaldes AI'en ikke.",
          'Et notat, der beskriver et indgreb, fx "Holdt borgeren fast i armene, da han slog ud efter en medbeboer". Tilføj også et neutralt notat for at se, at det ikke bliver et fund.',
          'Sammenfoldet fund: et Skema 1 (ikke annulleret) for samme episode, med indgrebets starttidspunkt tæt på notatets hændelsesdato. Kræver læseret til magtanvendelser.',
        ],
      },
      {
        heading: 'Data',
        points: [
          'Gennemgangen kører, når du beder om det. Fund og afvisninger gemmes ikke.',
        ],
      },
    ],
  },
  story: {
    title: 'Om "Fortæl om vagten"',
    sections: [
      {
        heading: 'Hvad gør den?',
        points: [
          "Du fortæller med tale eller tekst om vagten, og AI'en foreslår, hvad der skal registreres: et dagbogsnotat, observationer og opfølgninger på delmål.",
          'Beskriver du et fysisk indgreb, gør den opmærksom på, at der måske skal udfyldes et Skema 1.',
          "Mangler der en værdi eller et klokkeslæt, spørger AI'en i stedet for at gætte. Højst tre spørgsmål, og du kan svare med tale eller tekst.",
        ],
      },
      {
        heading: 'Hvad bygger den på?',
        points: [
          'Kun det, du fortæller, plus enhedens aktive observationstyper og borgerens aktuelle delmål.',
          'Hvert forslag viser det citat fra din fortælling, det bygger på.',
          'Huskelisten ved siden af viser borgerens OBS-noter, dagens aftaler, enhedens observationstyper med seneste værdi fra de sidste 72 timer og borgerens delmål. Den hentes direkte fra Fasit uden AI.',
          'Når du har lavet forslag, viser huskelisten, hvor mange observationstyper og delmål forslagene berører. OBS-noter og aftaler tjekker du selv.',
        ],
      },
      {
        heading: 'Det skal du selv vurdere',
        points: [
          'Intet gemmes, før du har set forslagene igennem og valgt dem. Ret eller fravælg det, der ikke passer.',
          "Fortæller du ikke om noget, foreslår AI'en det heller ikke.",
        ],
      },
      {
        heading: testDataHeading,
        points: [
          'Dagbogsnotat: kræver ingen data på borgeren.',
          'Observationer: mindst én aktiv observationstype, der gælder for enheden eller alle enheder. Nævn typen og en værdi i fortællingen, fx "Hun sov kun 4 timer".',
          'Delmålsopfølgninger: en aktuel bestilling på enheden med et aktuelt delmål. Nævn noget, der handler om delmålet. Kræver læseret til delmål.',
          'Hint om Skema 1: fortæl om et fysisk indgreb, fx "Jeg måtte holde ham i armen".',
          'Spørgsmål: nævn en observationstype uden værdi, fx "Hun sov dårligt", eller en episode uden klokkeslæt.',
          'Huskeliste: en aktiv OBS-note, en aftale i dag eller en observation inden for 72 timer. Uden observationstyper på enheden vises faste emner. Kræver læseret til borgeren, observationer og delmål.',
          'Tjekliste efter forslag: mindst én aktiv observationstype på enheden eller et aktuelt delmål. Nævn nogle af dem og udelad andre, så ser du både flueben og manglende punkter. Tryk "Fortæl mere", tilføj et manglende punkt og lav forslag igen.',
        ],
      },
      {
        heading: 'Data',
        points: [
          'Optagelsen omsættes til tekst og bruges kun til forslagene. Det er de registreringer, du gemmer, der bliver i Fasit.',
        ],
      },
    ],
  },
  unitPatterns: {
    title: 'Om mønstre på tværs af borgerne',
    sections: [
      {
        heading: 'Hvad gør den?',
        points: [
          'Finder gentagelser på tværs af enhedens borgere, fx optrapninger på samme tidspunkt, samme situation før indgreb eller de samme medarbejdere i flere indgreb.',
          'Viser op til 5 mønstre med et forslag til forebyggelse. Klik på et mønster for at se borgerne i tabellen.',
        ],
      },
      {
        heading: 'Hvad bygger den på?',
        points: [
          'Zoneskift i trafiklyset med observationstekst og typiske triggere samt registrerede magtanvendelser fra de seneste 60 dage.',
          'Kun borgere med en aktuel bestilling på enheden. Dagbogen indgår ikke.',
          'Et mønster vises kun, hvis det bygger på mindst to hændelser.',
        ],
      },
      {
        heading: 'Det skal du selv vurdere',
        points: [
          "Mønstrene er forslag at tale ud fra, ikke konklusioner. AI'en kender kun det, der er registreret.",
          'Forslagene til forebyggelse er generelle og skal tilpasses borgerne.',
        ],
      },
      {
        heading: testDataHeading,
        points: [
          'Flere borgere med en aktuel bestilling på enheden, hver med et trafiklys på enheden. Udfyld typiske triggere i gul og rød zone.',
          'Zoneskift: skift trafiklysets vurdering flere gange inden for de seneste 60 dage, og skriv en observation ved hvert skift – gerne samme ugedag, tidspunkt eller situation hos flere borgere.',
          'Magtanvendelser: Skema 1 (ikke annulleret) inden for 60 dage med indgrebstype, sted, deltagende medarbejdere og hvad der gik forud. Genbrug fx samme medarbejder eller situation.',
          "Mindst to hændelser (zoneskift eller magtanvendelser) i alt, ellers kaldes AI'en ikke. Kræver læseret til borgere og magtanvendelser.",
        ],
      },
      {
        heading: 'Data',
        points: [
          "AI'en får ikke borgernes navne eller CPR-numre; de sendes som Borger 1, Borger 2 osv. Medarbejdernes navne fra magtanvendelserne sendes med.",
          'Analysen kører kun, når du trykker på knappen, og gemmes ikke.',
        ],
      },
    ],
  },
};

export function AiFeatureInfo({ feature }: { feature: AiFeature }) {
  const [open, setOpen] = useState(false);
  const description = descriptions[feature];

  return (
    <>
      <Tooltip title="Om denne AI-funktion">
        <IconButton
          size="small"
          aria-label={description.title}
          onClick={() => setOpen(true)}
        >
          <FontAwesomeIcon icon={faCircleInfo} />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{description.title}</DialogTitle>
        <DialogContent dividers>
          {description.sections.map(section => (
            <Box
              key={section.heading}
              sx={{ mb: 2, '&:last-child': { mb: 0 } }}
            >
              <Typography variant="subtitle2">{section.heading}</Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
                {section.points.map(point => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={point}
                    sx={{ mb: 0.5 }}
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Luk</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
